import React, {
  useState,
  ReactNode,
  useRef,
  useCallback,
  useEffect,
} from "react";
import {
  Box,
  ClickAwayListener,
  Popper,
  useTheme,
  SxProps,
} from "@mui/material";

interface DropdownItem {
  jsx: ReactNode;
  name?: string;
}

interface ActionsDropdownProps {
  triggerButton: (
    handleClick: (event: React.MouseEvent<HTMLElement>) => void,
    open: boolean
  ) => ReactNode;
  items: DropdownItem[];
  shouldRenderItem?: (item: DropdownItem) => boolean;
  children?: ReactNode;
  width?: string;
  zoomLevel?: number;
  onActionClick?: (closeHandler: () => void) => void;
  popperSx?: SxProps;
}

export const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  triggerButton,
  items,
  shouldRenderItem = () => true,
  children,
  width,
  zoomLevel,
  onActionClick,
  popperSx,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl]
  );

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (
        (contentRef.current &&
          contentRef.current.contains(event.target as Node)) ||
        (anchorEl && anchorEl.contains(event.target as Node))
      ) {
        return;
      }
      setAnchorEl(null);
    },
    [anchorEl]
  );

  const closeDropdown = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    if (onActionClick) {
      onActionClick(closeDropdown);
    }
  }, [onActionClick, closeDropdown]);

  const open = Boolean(anchorEl);

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      {triggerButton(handleClick, open)}
      <Popper
        sx={{
          zIndex: 999,
          zoom: zoomLevel ? `${zoomLevel}%` : 1,
          padding: theme.nawy.spacing.xs,

          position: "relative",
          "& .MuiAutocomplete-popper": {
            position: "fixed !important",
            zIndex: 1000,
          },
          ...popperSx,
        }}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        modifiers={[
          {
            name: "flip",
            enabled: false,
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              rootBoundary: "viewport",
            },
          },
        ]}
      >
        <ClickAwayListener
          mouseEvent={"onMouseUp"}
          onClickAway={handleClickAway}
        >
          <Box
            ref={contentRef}
            sx={{
              width: width ? width : "fit-content",
              border: "1px solid #D0D5DD",
              backgroundColor: "white",
              borderRadius: theme.nawy.radius.lg,
              display: "flex",
              flexDirection: "column",
              boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.10)",
              marginTop: theme.nawy.spacing.xs,
              padding: theme.nawy.spacing.xs,
            }}
          >
            {children}
            {items &&
              items.map((item, index) =>
                shouldRenderItem(item) ? (
                  <Box key={index}>{item.jsx}</Box>
                ) : null
              )}
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
